import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Form from "antd/es/form";
import notification from "antd/es/notification";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import { Modal, TextField } from "@mapmycustomers/ui";

import LockedEntityType from "@app/component/createEditEntity/Activity/component/LockedFormFields/LockedEntityType";
import { getBouncedEmailEntity, isBouncedEmailEntityEmailUpdating } from "@app/store/notification";
import { updateBouncedEmailEntityEmail } from "@app/store/notification/actions";
import { RootState } from "@app/store/rootReducer";
import { getEntityTypeDisplayName } from "@app/util/ui";

import messages from "./messages";

interface FormValues {
  email: string;
}

type UpdateEmailModalProps = {
  label: string;
  onHide: () => void;
  required: boolean;
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const UpdateEmailModal: React.FC<UpdateEmailModalProps> = ({
  entity,
  entityEmailUpdating,
  label,
  onHide,
  onUpdateEmail,
  required,
}) => {
  const intl = useIntl();

  const [form] = Form.useForm<FormValues>();

  const handleSaveClick = useCallback(() => {
    form.submit();
  }, [form]);

  const handleFormFinish = useCallback(
    ({ email }: FormValues) => {
      onUpdateEmail({
        email,
        onSuccess: () => {
          notification.success({
            message: intl.formatMessage(messages.entityUpdatedSuccessfullyMessage, {
              entityName: getEntityTypeDisplayName(intl, entity.entity),
            }),
          });
          onHide();
        },
      });
    },
    [intl, entity.entity, onHide, onUpdateEmail]
  );

  return (
    <Modal
      cancelButtonProps={{
        disabled: entityEmailUpdating,
      }}
      destroyOnClose={true}
      okButtonProps={{
        disabled: entityEmailUpdating,
        loading: entityEmailUpdating,
      }}
      okText={intl.formatMessage(messages.saveButtonLabel)}
      onCancel={onHide}
      onOk={handleSaveClick}
      open
      size="small"
      title={intl.formatMessage(messages.updateEmailTitle)}
      type="info"
    >
      <Form
        form={form}
        initialValues={{ email: entity.email }}
        layout="vertical"
        onFinish={handleFormFinish}
      >
        <Space direction="vertical" size="middle">
          <span>{intl.formatMessage(messages.updateEmailDescription)}</span>
          <LockedEntityType
            entityType={entity.entity}
            label={getEntityTypeDisplayName(intl, entity.entity)}
            placeholder={entity.name}
          />
          <Form.Item
            label={label}
            name="email"
            required={required}
            rules={[{ required, type: "email" }]}
          >
            <TextField addonBefore={<FontAwesomeIcon icon={faEnvelope} />} />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  entity: getBouncedEmailEntity(state)!,
  entityEmailUpdating: isBouncedEmailEntityEmailUpdating(state)!,
});

const mapDispatchToProps = {
  onUpdateEmail: updateBouncedEmailEntityEmail.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateEmailModal);
